import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { map, of, take } from 'rxjs';
import { SessionAuthService } from '../services/auth/session-auth.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { APP_AUTH_CONFIG_ID } from '../services/auth/admin-auth.constants';
import { isNativeInvitationRedirect } from '../services/auth/app-redirect-uri';
import { environment } from '../../environments/environment';

/** Process an invitation OIDC callback before the completion API is called. */
export const invitationCompletionGuard: CanActivateFn = () => {
    const session = inject(SessionAuthService);
    const oidc = inject(OidcSecurityService);
    const router = inject(Router);
    const callbackParams = new URL(window.location.href).searchParams;
    const authorize = () =>
        oidc.authorize(APP_AUTH_CONFIG_ID, {
            redirectUrl: `${window.location.origin}/invitation/complete`,
            customParams: { ui_locales: 'it' }
        });
    // Native organization redirects have no OIDC result. Obtain the registered
    // account from Keycloak before consulting tokens left by another account.
    if (
        isNativeInvitationRedirect(window.location.href, environment.identityUrl) ||
        (!callbackParams.has('code') && !callbackParams.has('error'))
    ) {
        authorize();
        return of(false);
    }
    return session.check().pipe(
        take(1),
        map((authenticated) => {
            if (authenticated) return true;
            if (session.failure()) return router.parseUrl('/unauthorized');
            authorize();
            return false;
        })
    );
};

export const oauthAppGuard: CanActivateFn = () => {
    const session = inject(SessionAuthService);
    const router = inject(Router);
    if (isNativeInvitationRedirect(window.location.href, environment.identityUrl)) {
        return of(router.parseUrl('/invitation/complete'));
    }
    return session.check().pipe(
        take(1),
        map((authenticated) =>
            authenticated
                ? session.emailVerificationRequired()
                    ? router.parseUrl('/invitation/complete')
                    : true
                : router.parseUrl(session.failure() ? '/unauthorized' : '/')
        )
    );
};

export const reverseAuthGuard: CanActivateFn = () => {
    const session = inject(SessionAuthService);
    const router = inject(Router);
    return session.check().pipe(
        take(1),
        map((authenticated) =>
            authenticated
                ? router.parseUrl(session.emailVerificationRequired() ? '/invitation/complete' : '/app')
                : session.failure()
                  ? router.parseUrl('/unauthorized')
                  : true
        )
    );
};
